import React, { useState } from 'react';
import { useTheme } from './hooks/useTheme';
import { useLanguage } from './hooks/useLanguage';
import HomePage from './pages/HomePage';
import FeaturesPage from './pages/FeaturesPage';
import PricingPage from './pages/PricingPage';
import ResourcesPage from './pages/ResourcesPage';
import ContactPage from './pages/ContactPage';
import DemoPage from './pages/DemoPage';

function App() {
  const { theme, toggleTheme } = useTheme();
  const { language, changeLanguage, t } = useLanguage();
  const [currentPage, setCurrentPage] = useState('home');

  const renderPage = () => {
    switch(currentPage) {
      case 'products':
        return <div className="p-8">
          <h1 className="text-3xl font-bold mb-6">Products</h1>
          <ul className="space-y-4">
            <li>
              <button className="text-blue-600 underline" onClick={() => setCurrentPage('product-ai-suite')}>Acentrical AI Suite</button>
              <span className="ml-2 text-gray-600">- Enterprise AI platform for analytics, automation, and NLP</span>
            </li>
            <li>
              <button className="text-blue-600 underline" onClick={() => setCurrentPage('product-robotics-os')}>Robotics OS</button>
              <span className="ml-2 text-gray-600">- Integration and control for industrial robots and IoT</span>
            </li>
            <li>
              <button className="text-blue-600 underline" onClick={() => setCurrentPage('product-cloud-hub')}>CloudHub Platform</button>
              <span className="ml-2 text-gray-600">- Scalable cloud infrastructure and secure API management</span>
            </li>
            <li>
              <button className="text-blue-600 underline" onClick={() => setCurrentPage('product-security-suite')}>Security Suite</button>
              <span className="ml-2 text-gray-600">- Encryption, access control, and threat detection</span>
            </li>
          </ul>
        </div>;
      case 'services':
        return <div className="p-8">
          <h1 className="text-3xl font-bold mb-6">Services</h1>
          <ul className="space-y-4">
            <li>
              <button className="text-blue-600 underline" onClick={() => setCurrentPage('service-consulting')}>Integration Consulting</button>
              <span className="ml-2 text-gray-600">- Expert guidance for digital transformation and system integration</span>
            </li>
            <li>
              <button className="text-blue-600 underline" onClick={() => setCurrentPage('service-custom-dev')}>Custom Development</button>
              <span className="ml-2 text-gray-600">- Tailored software solutions for unique business needs</span>
            </li>
            <li>
              <button className="text-blue-600 underline" onClick={() => setCurrentPage('service-support')}>Enterprise Support</button>
              <span className="ml-2 text-gray-600">- 24/7 support and SLA guarantees</span>
            </li>
            <li>
              <button className="text-blue-600 underline" onClick={() => setCurrentPage('service-training')}>Training & Enablement</button>
              <span className="ml-2 text-gray-600">- Workshops and onboarding for teams and developers</span>
            </li>
          </ul>
        </div>;
      case 'product-ai-suite':
        return <FeaturesPage t={t} />;
      case 'product-robotics-os':
        return <FeaturesPage t={t} />;
      case 'product-cloud-hub':
        return <FeaturesPage t={t} />;
      case 'product-security-suite':
        return <FeaturesPage t={t} />;
      case 'service-consulting':
        return <FeaturesPage t={t} />;
      case 'service-custom-dev':
        return <FeaturesPage t={t} />;
      case 'service-support':
        return <FeaturesPage t={t} />;
      case 'service-training':
        return <FeaturesPage t={t} />;
      case 'features':
        return <FeaturesPage t={t} />;
      case 'pricing':
        return <PricingPage t={t} />;
      case 'resources':
        return <ResourcesPage t={t} />;
      case 'contact':
        return <ContactPage t={t} />;
      case 'demo':
        return <DemoPage t={t} />;
      default:
        return <HomePage setCurrentPage={setCurrentPage} t={t} />;
    }
  };

  return (
    <div className={`min-h-screen transition-colors duration-200 ${theme === 'dark' ? 'dark bg-gray-900 text-white' : 'bg-white text-gray-900'}`}>
      <nav className="flex flex-col sm:flex-row sm:justify-between sm:items-center px-4 sm:px-8 py-4 sm:py-6 bg-gray-100 dark:bg-gray-800 space-y-4 sm:space-y-0">
<button
  className="flex items-center space-x-3 mb-2 sm:mb-0 focus:outline-none"
  onClick={() => setCurrentPage('home')}
  aria-label="Go to Home"
  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
>
  <img src="/logo2.svg" alt="Company Logo" className="h-8 w-8" />
  <span className="font-bold text-lg">{t('appName')}</span>
</button>
        <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-6">
          <button className="hover:underline" onClick={() => setCurrentPage('home')}>{t('navigation.home')}</button>
          <button className="hover:underline" onClick={() => setCurrentPage('products')}>{t('navigation.products')}</button>
          <button className="hover:underline" onClick={() => setCurrentPage('services')}>{t('navigation.services')}</button>
          <button className="hover:underline" onClick={() => setCurrentPage('features')}>{t('navigation.features')}</button>
          <button className="hover:underline" onClick={() => setCurrentPage('pricing')}>{t('navigation.pricing')}</button>
          <button className="hover:underline" onClick={() => setCurrentPage('resources')}>{t('navigation.resources')}</button>
          <button className="hover:underline" onClick={() => setCurrentPage('contact')}>{t('navigation.contact')}</button>
          <button
            className="ml-6 px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 flex items-center"
            onClick={toggleTheme}
            aria-label="Toggle dark mode"
          >
            {theme === 'dark' ? (
              // Sun icon for light mode
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="2" fill="currentColor" />
                <path stroke="currentColor" strokeWidth="2" d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
              </svg>
            ) : (
              // Moon icon for dark mode
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M21 12.75A9 9 0 1111.25 3c0 .41.34.75.75.75a7.5 7.5 0 007.5 7.5c.41 0 .75.34.75.75z" />
              </svg>
            )}
          </button>
          <select
            className="ml-4 px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            value={language}
            onChange={e => changeLanguage(e.target.value as 'en' | 'zh-Hant' | 'zh-Hans' | 'es' | 'fr')}
            aria-label="Select language"
          >
            <option value="en">English</option>
            <option value="zh-Hant">繁體中文</option>
            <option value="zh-Hans">简体中文</option>
            <option value="es">Español</option>
            <option value="fr">Français</option>
          </select>
        </div>
      </nav>
      {renderPage()}
    </div>
  );
}

export default App;
